/**
 * components/import/statements/AccountPickerCard.jsx — Phase 84.3 slice-1
 *
 * Calm picker that sits above the dropzone and asks "Which account does
 * this statement belong to?". If the user has zero accounts, we show an
 * inline "+ Add account" CTA. Disabled when the user is already
 * cap-exhausted (the cap UI takes precedence). Extracted verbatim from
 * pages/CSVImport.js (was internal function `AccountPickerCard`). Zero
 * behavior change.
 */
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Wallet, Plus } from 'lucide-react';
import NewAccountInlineDialog from './NewAccountInlineDialog';

export default function AccountPickerCard({ accounts, accountId, setAccountId, accountsLoaded, reloadAccounts, capExhausted }) {
  const [addOpen, setAddOpen] = useState(false);
  const hasAccounts = (accounts || []).length > 0;
  return (
    <Card className="rounded-2xl border-border/70" data-testid="single-account-picker">
      <CardContent className="p-4 sm:p-5 flex flex-wrap items-center gap-3">
        <div className="h-9 w-9 rounded-xl bg-accent grid place-items-center shrink-0">
          <Wallet className="h-4.5 w-4.5 text-primary" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-sm font-medium">For which account?</div>
          <div className="text-[11.5px] text-muted-foreground">Every transaction we import will be linked to this account.</div>
        </div>
        {!accountsLoaded ? (
          <div className="text-xs text-muted-foreground">Loading&hellip;</div>
        ) : !hasAccounts ? (
          <>
            <span className="text-xs text-muted-foreground hidden sm:inline">No accounts yet ,</span>
            <Button
              size="sm"
              variant="secondary"
              className="rounded-xl"
              onClick={() => setAddOpen(true)}
              disabled={capExhausted}
              data-testid="single-add-account-button"
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Add an account
            </Button>
          </>
        ) : (
          <>
            <Select value={accountId || undefined} onValueChange={(v) => setAccountId(v)} disabled={capExhausted}>
              <SelectTrigger className="rounded-xl w-[220px]" data-testid="single-account-select">
                <SelectValue placeholder="Choose account…" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    <span className="truncate">{a.name}</span>
                    {a.last4 && <span className="ml-1.5 text-muted-foreground tabular-nums text-[11px]">&bull;&bull;{a.last4}</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="ghost"
              className="rounded-xl h-9"
              onClick={() => setAddOpen(true)}
              disabled={capExhausted}
              data-testid="single-add-another-account-button"
              title="Add a new account"
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </>
        )}
      </CardContent>
      <NewAccountInlineDialog
        open={addOpen}
        setOpen={setAddOpen}
        onCreated={async (newId) => {
          await reloadAccounts();
          if (newId) setAccountId(newId);
        }}
      />
    </Card>
  );
}
